import { merge } from 'lodash/fp'
import { toast } from '@wellesley/system-toast'
import { logger } from 'helpers/logger'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import {
  api,
  Rule,
  QueryTags,
  RuleOrderingRequest,
  fetchInterceptor,
  UserSettings,
} from '@wellesley/openapi'
import {
  setPlatformSettings,
  setProfileSettings,
} from '@wellesley/host-remote/api/actions'
import {
  flatToNestedSettings,
  type NestedUserSettings,
} from '../helpers/nestedUserSettings'
import type { ProfileSettingsState } from '../slices/profileSettingsSlice'
import { getErrorMsg } from '@wellesley/error-kit'
import type { RootState } from '@wellesley/store'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { platformSettingsSlice } from '../slices/platformSettingsSlice'
import { brandingSettingsKeys, instanceLegalSettingsKeys } from '../keys'

const settingsApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1Settings: {
      extraOptions: {
        validateInitialDataParams: ({
          requestArgs: {
            params: { keys },
          },
        }: {
          requestArgs: { params: { keys: string[] } }
        }) => {
          return keys.length > 0
        },
        initialDataKeyResolver: ({
          requestArgs: {
            params: { keys },
          },
        }: {
          requestArgs: { params: { keys: string[] } }
        }) => {
          if (keys.toString() === brandingSettingsKeys.toString()) {
            return 'brandingSettings'
          } else if (keys.toString() === instanceLegalSettingsKeys.toString()) {
            return 'legalSettings'
          }
        },
      },
      providesTags: ['Settings'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(platformSettingsSlice.actions.setPending(true))
        try {
          const response = await handleBasicResponse(queryFulfilled)

          response && dispatch(setPlatformSettings(response.data))
        } catch (e) {
          logger.error(`Platform settings error:`, e)
        } finally {
          dispatch(platformSettingsSlice.actions.setPending(false))
        }
      },
    },
    getApiV1SettingsUser: {
      extraOptions: {
        initialDataKey: 'currentUserSettings',
      },
      providesTags: ['UserSettings', 'USER_CHANGED'],
      async onCacheEntryAdded(
        _,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded
        const off = fetchInterceptor.on<UserSettings, UserSettings>({
          url: '/api/v1/settings/user',
          callback: ({ request, response, method }) => {
            if (method === 'DELETE') {
              dispatch(api.util.invalidateTags(['UserSettings']))
            }

            const settings = ['PUT', 'POST'].includes(method || '')
              ? request.body
              : response.body

            if (settings) {
              const nestedSettings = flatToNestedSettings(settings)

              if (nestedSettings) {
                dispatch(setProfileSettings(nestedSettings))
              }

              updateCachedData((draft) => {
                Object.assign(draft, settings)
              })
            }
          },
        })
        await cacheEntryRemoved
        off()
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await handleBasicResponse(queryFulfilled)
          if (response?.data) {
            const nestedSettings = flatToNestedSettings(response.data)
            if (nestedSettings) {
              dispatch(setProfileSettings(nestedSettings))
            }
          }
        } catch (e) {
          logger.error(`Get profile settings error:`, e)
        }
      },
    },
    putApiV1Settings: {
      async onQueryStarted(data, { dispatch, queryFulfilled, getState }) {
        const lastArg = api.util.selectCachedArgsForQuery(
          getState(),
          'getApiV1Settings',
        )[0]

        const patchResult = dispatch(
          api.util.updateQueryData(
            'getApiV1Settings',
            lastArg || { keys: [] },
            (draft) => {
              Object.assign(draft, merge(draft, data.platformSettings))
            },
          ),
        )
        try {
          await queryFulfilled
          const state = getState() as RootState
          const currentSettings =
            platformSettingsSlice.selectors.selectSettings({
              platformSettings: state.platformSettings,
            })

          dispatch(
            setPlatformSettings(
              merge(currentSettings || {}, data.platformSettings),
            ),
          )
          toast.success('Updated')
        } catch {
          patchResult.undo()
          toast.error('Failed to update settings')
        }
      },
    },
    putApiV1SettingsUser: {
      async onQueryStarted(data, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState
        const profileSettings = state.profileSettings as
          | ProfileSettingsState
          | undefined
        const currentNestedSettings = profileSettings?.data

        const lastArg = api.util.selectCachedArgsForQuery(
          state,
          'getApiV1SettingsUser',
        )[0]

        const patchResult = dispatch(
          api.util.updateQueryData(
            'getApiV1SettingsUser',
            lastArg || { keys: [] },
            (draft) => {
              Object.assign(
                draft as { [key: string]: string },
                merge(draft, data.userSettings),
              )
            },
          ),
        )

        const updateNested = flatToNestedSettings(data.userSettings)
        if (updateNested) {
          const optimisticNestedSettings = merge(
            currentNestedSettings || { user: {} },
            updateNested,
          ) as NestedUserSettings
          dispatch(setProfileSettings(optimisticNestedSettings))
        }

        try {
          await queryFulfilled
          toast.success('Updated')
        } catch {
          patchResult.undo()
          if (currentNestedSettings) {
            dispatch(setProfileSettings(currentNestedSettings))
          }
        }
      },
    },

    getApiV1Rules: {
      providesTags: [QueryTags.SERVER_RULES],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1Rules: {
      invalidatesTags: [QueryTags.SERVER_RULES],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Rule created')
        } catch (error) {
          toast.error(
            `Failed to create rule ${getErrorMsg(error as FetchBaseQueryError)}`,
          )
        }
      },
    },
    putApiV1Rules: {
      async onQueryStarted({ rule }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          api.util.updateQueryData('getApiV1Rules', undefined, (draft) => {
            const index = draft.findIndex((r) => r.id === rule.id)

            if (index !== -1) {
              draft[index] = {
                ...draft[index],
                ...rule,
              }
            }
          }),
        )

        try {
          await queryFulfilled
          toast.success('Rule updated')
        } catch (error) {
          patch.undo()
          toast.error(
            `Failed to update rule ${getErrorMsg(error as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1RulesReorder: {
      async onQueryStarted(
        { orderedRules }: { orderedRules: Rule[]; body: RuleOrderingRequest[] },
        { dispatch, queryFulfilled },
      ) {
        const rulesWithUpdatedOrdering = orderedRules.map((rule, index) => ({
          ...rule,
          ordering: index + 1,
        }))

        const patch = dispatch(
          api.util.updateQueryData('getApiV1Rules', undefined, () => {
            return rulesWithUpdatedOrdering
          }),
        )

        try {
          await queryFulfilled
        } catch (error) {
          patch.undo()
          toast.error(
            `Failed to reorder rules ${getErrorMsg(error as FetchBaseQueryError)}`,
          )
        }
      },
    },
    deleteApiV1RulesById: {
      async onQueryStarted({ id: ruleId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          api.util.updateQueryData('getApiV1Rules', undefined, (draft) => {
            const index = draft.findIndex((rule) => rule.id === ruleId)
            if (index !== -1) {
              draft.splice(index, 1)
            }
          }),
        )

        try {
          await queryFulfilled
          toast.success('Rule deleted')
        } catch (error) {
          patch.undo()
          toast.error(
            `Failed to delete rule ${getErrorMsg(error as FetchBaseQueryError)}`,
          )
        }
      },
    },
  },
})

export const {
  useGetApiV1SettingsQuery,
  useGetApiV1SettingsUserQuery,
  usePutApiV1SettingsMutation,
  usePutApiV1SettingsUserMutation,
  useLazyGetApiV1SettingsUserQuery,
  useGetApiV1InstanceSupportedLanguagesQuery,
  useGetApiV1RulesQuery,
  usePostApiV1RulesMutation,
  usePutApiV1RulesMutation,
  usePostApiV1RulesReorderMutation,
  useDeleteApiV1RulesByIdMutation,
} = settingsApi
